<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { changelog, closeChangelog, textOf } from '../changelog';
import { locale, t } from '../i18n';
import Icon from './Icon.vue';
import NoteText from './NoteText.vue';

/**
 * "What's new": after an update the notes this browser has not seen, once; from the menu the whole
 * history. Closing it (button, Esc, a click outside) marks the shown notes as seen.
 */
const list = computed(() => (changelog.open === 'new' ? changelog.fresh : changelog.entries));
const button = ref<HTMLButtonElement | null>(null);
watch(
  () => changelog.open,
  async (o) => {
    if (!o) return;
    await nextTick();
    button.value?.focus();
  },
);
const date = (iso: string) => new Date(iso).toLocaleDateString(locale.value === 'ru' ? 'ru-RU' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
</script>

<template>
  <div v-if="changelog.open" class="wn-backdrop" @pointerdown.self="closeChangelog" @keydown.esc.stop="closeChangelog">
    <div class="wn float card" role="dialog" aria-modal="true" aria-labelledby="wn-title" data-dialog="whats-new">
      <header>
        <div class="mark"><Icon name="info" :size="18" /></div>
        <h2 id="wn-title">{{ changelog.open === 'new' ? t('changelog.title') : t('changelog.history') }}</h2>
      </header>
      <div class="entries">
        <article v-for="e in list" :key="e.id" :data-version="e.version">
          <div class="head">
            <span class="ver">{{ e.version }}</span>
            <time class="muted">{{ date(e.publishedAt) }}</time>
          </div>
          <h3>{{ textOf(e.title) }}</h3>
          <NoteText v-if="textOf(e.body)" :text="textOf(e.body)" />
        </article>
      </div>
      <button ref="button" class="pill primary tall ok" data-act="close-whats-new" @click="closeChangelog">{{ changelog.open === 'new' ? t('changelog.gotIt') : t('common.close') }}</button>
    </div>
  </div>
</template>

<style scoped>
.wn-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: grid;
  place-items: center;
  padding: 16px;
  background: color-mix(in srgb, var(--backdrop) 70%, transparent);
}
.wn {
  width: min(480px, 100%);
  max-height: min(640px, 100%);
  padding: 22px 24px 20px;
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.mark {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent);
}
h2 {
  margin: 0;
  font-size: 18px;
}
.entries {
  overflow: auto;
  display: grid;
  gap: 18px;
  padding-right: 4px;
}
article {
  display: grid;
  gap: 6px;
}
article + article {
  padding-top: 16px;
  border-top: 1px solid var(--border);
}
.head {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}
.ver {
  padding: 1px 9px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 600;
}
h3 {
  margin: 0;
  font-size: 15px;
}
.ok {
  align-self: flex-end;
  min-width: 120px;
  justify-content: center;
}
</style>
